/* eslint-disable @atlaskit/ui-styling-standard/use-compiled */
import { css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import {
	akEditorFullPageDefaultFontSize,
	akEditorFullPageDenseFontSize,
} from '@atlaskit/editor-shared-styles';
import { token } from '@atlaskit/tokens';

import { EDITOR_LIST_DENSE_GAP } from './list';
import {
	blanketSelectionStyles,
	boxShadowSelectionStyles,
	dangerBackgroundStyles,
	dangerBorderStyles,
	hideNativeBrowserTextSelectionStyles,
} from './selectionStyles';

const akEditorLineHeight = 1.714;
const akEditorSelectedNodeClassName = 'ak-editor-selected-node';

export const TaskDecisionSharedCssClassName = {
	DECISION_CONTAINER: 'decisionItemView-content-wrap',
	TASK_CONTAINER: 'taskItemView-content-wrap',
	//NOTE: value is a selector (data attribute), not a class name
	TASK_LIST_CONTAINER: '[data-node-type="actionList"]',
	TASK_ITEM: 'task-item',
	TASK_CHECKBOX_CONTAINER: 'task-item-checkbox-wrap',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const tasksAndDecisionsStyles: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ProseMirror': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-values
		[`.taskItemView-content-wrap, .${TaskDecisionSharedCssClassName.DECISION_CONTAINER}`]: {
			position: 'relative',
			minWidth: 48,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-values
		[`.${TaskDecisionSharedCssClassName.DECISION_CONTAINER}`]: {
			marginTop: 0,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-values
		[`.${TaskDecisionSharedCssClassName.TASK_CONTAINER}`]: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			"span[contenteditable='false']": {
				height: `${akEditorLineHeight}em`,
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-values
		[`.${TaskDecisionSharedCssClassName.TASK_ITEM}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: akEditorLineHeight,
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[data-task-local-id]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"span[contenteditable='false']": {
			height: `${akEditorLineHeight}em`,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"span[contenteditable='false'] + div": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${akEditorLineHeight}em`,
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[data-task-list-local-id]': {
		margin: `${token('space.150')} 0 0 0`,

		// If task item is not first in the list then set margin top to 4px.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'div + div': {
			marginTop: token('space.050'),
		},
	},

	// If task list is not first in the document then set margin top to 4px.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[data-task-list-local-id] div[data-task-list-local-id]': {
		marginTop: token('space.050'),
		marginLeft: token('space.300'),
	},

	// When action list is inside panel
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ak-editor-panel__content': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors,@atlaskit/ui-styling-standard/no-unsafe-selectors
		'> div[data-task-list-local-id]:first-child': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			margin: '0 !important',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
const TASKLIST_CONTAINER_MARGIN_DENSE = `max(0px, calc(10px + (var(--ak-editor-base-font-size, ${akEditorFullPageDefaultFontSize}px) - ${akEditorFullPageDenseFontSize}px) * (2 / 3)))`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const getDenseTasksAndDecisionsStyles = (baseFontSize?: number): SerializedStyles => {
	if (!baseFontSize || baseFontSize === akEditorFullPageDefaultFontSize) {
		return css({});
	}

	return css({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
		'.ProseMirror': {
			// Task lists
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
			[TaskDecisionSharedCssClassName.TASK_LIST_CONTAINER]: {
				// Task lists: container top margin
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				marginTop: TASKLIST_CONTAINER_MARGIN_DENSE,
			},

			// Task lists: sibling items and nested lists
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
			[`${TaskDecisionSharedCssClassName.TASK_LIST_CONTAINER} > * + *`]: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
				marginTop: EDITOR_LIST_DENSE_GAP,
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
			[`${TaskDecisionSharedCssClassName.TASK_LIST_CONTAINER} ${TaskDecisionSharedCssClassName.TASK_LIST_CONTAINER}, .${TaskDecisionSharedCssClassName.TASK_CONTAINER} .${TaskDecisionSharedCssClassName.TASK_CONTAINER}`]:
				{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
					marginTop: EDITOR_LIST_DENSE_GAP,
				},
		},
	});
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const decisionStyles: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
	[`.${akEditorSelectedNodeClassName} > [data-decision-wrapper], ol[data-node-type='decisionList'].${akEditorSelectedNodeClassName}`]:
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		[
			{
				borderRadius: token('radius.small'),
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			boxShadowSelectionStyles,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			blanketSelectionStyles,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			hideNativeBrowserTextSelectionStyles,
		],

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.danger': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
		[`.${TaskDecisionSharedCssClassName.DECISION_CONTAINER}.${akEditorSelectedNodeClassName} > div`]:
			{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				boxShadow: `0 0 0 1px ${token('color.border.danger')}`,
				backgroundColor: token('color.blanket.danger'),
				'&::after': {
					content: 'none', // reset the Blanket selection style
				},
			},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"]': {
		listStyleType: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper]': {
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'row',
		margin: `${token('space.100')} 0 0 0`,
		padding: token('space.100'),
		paddingLeft: token('space.150'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
		position: 'relative',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"]':
		{
			flex: '0 0 16px',
			height: '16px',
			width: '16px',
			margin: `${token('space.050')} ${token('space.150')} 0 0`,
			color: token('color.icon.subtle'),
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"]:not(:has([data-empty]):not(:has([data-type-ahead]))) > [data-decision-wrapper] > [data-component="icon"]':
		{
			color: token('color.icon.success'),
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span':
		{
			display: 'inline-block',
			flexShrink: 0,
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Mirroring icon styles
			lineHeight: 1,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span > svg':
		{
			overflow: 'hidden',
			pointerEvents: 'none',
			color: 'currentColor',
			verticalAlign: 'bottom',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="placeholder"]':
		{
			margin: `0 0 0 calc(${token('space.100')} * 3.5)`,
			position: 'absolute',
			color: token('color.text.subtlest'),
			pointerEvents: 'none',
			textOverflow: 'ellipsis',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			maxWidth: 'calc(100% - 50px)',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"]:not(:has([data-empty]):not(:has([data-type-ahead]))) > [data-decision-wrapper] > [data-component="placeholder"]':
		{
			display: 'none',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="content"]':
		{
			margin: 0,
			wordWrap: 'break-word',
			minWidth: 0,
			flex: '1 1 auto',
		},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const decisionDangerStyles: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors
	[`.${akEditorSelectedNodeClassName}.danger > [data-decision-wrapper], ol[data-node-type='decisionList'].${akEditorSelectedNodeClassName}.danger`]:
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		[dangerBackgroundStyles, dangerBorderStyles],
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const decisionIconWithVisualRefresh: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span >  svg[data-icon-source="legacy"]':
		{
			display: 'none',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span':
		{
			boxSizing: 'border-box',
			paddingInlineEnd: 'var(--ds--button--new-icon-padding-end, 0)',
			paddingInlineStart: 'var(--ds--button--new-icon-padding-start, 0)',
			'@media screen and (forced-colors: active)': {
				color: 'canvastext',
				filter: 'grayscale(1)',
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span > svg':
		{
			width: token('space.300'),
			height: token('space.300'),
		},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const decisionIconWithoutVisualRefresh: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span >  svg[data-icon-source="refreshed"]':
		{
			display: 'none',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span':
		{
			width: '32px',
			height: '32px',
			'@media screen and (forced-colors: active)': {
				filter: 'grayscale(1)',
				color: 'canvastext',
				fill: 'canvas',
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="decisionItem"] > [data-decision-wrapper] > [data-component="icon"] > span > svg':
		{
			maxWidth: '100%',
			maxHeight: '100%',
			fill: token('elevation.surface'),
			width: '32px',
			height: '32px',
		},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const taskItemStyles: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"]': {
		listStyle: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"] [data-component="task-item-main"]': {
		display: 'flex',
		flexDirection: 'row',
		position: 'relative',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"] [data-component="placeholder"]': {
		position: 'absolute',
		color: token('color.text.subtlest'),
		margin: `0 0 0 calc(${token('space.100')} * 3)`,
		pointerEvents: 'none',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		maxWidth: 'calc(100% - 50px)',
		display: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	"[data-prosemirror-node-name='taskItem']:has([data-empty]):not(:has([data-type-ahead])) [data-component='placeholder']":
		{
			display: 'block',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"] [data-component="content"]': {
		margin: 0,
		wordWrap: 'break-word',
		minWidth: 0,
		flex: '1 1 auto',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const taskItemCheckboxStyles: SerializedStyles = css({
	/**
	 * Background
	 */
	'--local-background': token('color.background.input'),
	'--local-background-active': token('color.background.input.pressed'),
	'--local-background-checked': token('color.background.selected.bold'),
	'--local-background-checked-hover': token('color.background.selected.bold.hovered'),
	'--local-background-disabled': token('color.background.disabled'),
	'--local-background-hover': token('color.background.input.hovered'),
	/**
	 * Border
	 */
	'--local-border': token('color.border.input'),
	'--local-border-active': token('color.border'),
	'--local-border-checked': token('color.background.selected.bold'),
	'--local-border-checked-hover': token('color.background.selected.bold.hovered'),
	'--local-border-checked-invalid': token('color.border.danger'),
	'--local-border-disabled': token('color.background.disabled'),
	'--local-border-focus': token('color.border.focused'),
	'--local-border-hover': token('color.border.input'),
	'--local-border-invalid': token('color.border.danger'),
	/**
	 * Tick
	 */
	'--local-tick-active': token('color.icon.inverse'),
	'--local-tick-checked': token('color.icon.inverse'),
	'--local-tick-disabled': token('color.icon.disabled'),
	'--local-tick-rest': 'transparent',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
	[`[data-prosemirror-node-name="taskItem"] .${TaskDecisionSharedCssClassName.TASK_CHECKBOX_CONTAINER}, [data-prosemirror-node-name="blockTaskItem"] .${TaskDecisionSharedCssClassName.TASK_CHECKBOX_CONTAINER}`]:
		{
			flex: '0 0 24px',
			width: '24px',
			height: '24px',
			position: 'relative',
			alignSelf: 'start',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"& > input[type='checkbox']": {
				opacity: 0,
				width: '100%',
				height: '100%',
				zIndex: 1,
				cursor: 'pointer',
				outline: 'none',
				margin: 0,
				position: 'absolute',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'&[disabled]': {
					cursor: 'default',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'& + svg': {
					'--checkbox-background-color': 'var(--local-background)',
					'--checkbox-border-color': 'var(--local-border)',
					'--checkbox-tick-color': 'var(--local-tick-rest)',
					color: 'var(--checkbox-background-color)',
					fill: 'var(--checkbox-tick-color)',
					transition: 'color 0.2s ease-in-out, fill 0.2s ease-in-out',
					boxSizing: 'border-box',
					display: 'inline',
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',

					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
					'rect:first-of-type': {
						stroke: 'var(--checkbox-border-color)',
						strokeWidth: token('border.width'),
						transition: 'stroke 0.2s ease-in-out',
					},
				},

				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
				'&:focus + svg, &:checked:focus + svg': {
					borderRadius: token('radius.small', '0.25rem'),
					outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
					outlineOffset: token('space.negative.025'),
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'&:hover + svg': {
					'--checkbox-background-color': 'var(--local-background-hover)',
					'--checkbox-border-color': 'var(--local-border-hover)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'&:checked:hover + svg': {
					'--checkbox-background-color': 'var(--local-background-checked-hover)',
					'--checkbox-border-color': 'var(--local-border-checked-hover)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'&:checked + svg': {
					'--checkbox-background-color': 'var(--local-background-checked)',
					'--checkbox-border-color': 'var(--local-border-checked)',
					'--checkbox-tick-color': 'var(--local-tick-checked)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'&:active + svg': {
					'--checkbox-background-color': 'var(--local-background-active)',
					'--checkbox-border-color': 'var(--local-border-active)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'&:checked:active + svg': {
					'--checkbox-background-color': 'var(--local-background-active)',
					'--checkbox-border-color': 'var(--local-border-active)',
					'--checkbox-tick-color': 'var(--local-tick-active)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'&:disabled + svg, &:disabled:hover + svg, &:disabled:focus + svg, &:disabled:active + svg, &:disabled[data-invalid] + svg':
					{
						'--checkbox-background-color': 'var(--local-background-disabled)',
						'--checkbox-border-color': 'var(--local-border-disabled)',
						cursor: 'not-allowed',
						pointerEvents: 'none',
					},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
				'&:disabled:checked + svg': {
					'--checkbox-tick-color': 'var(--local-tick-disabled)',
				},
			},
		},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const taskItemStylesWithBlockTaskItem: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"], [data-prosemirror-node-name="blockTaskItem"]': {
		listStyle: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"] [data-component="task-item-main"], [data-prosemirror-node-name="blockTaskItem"] [data-component="task-item-main"]':
		{
			display: 'flex',
			flexDirection: 'row',
			position: 'relative',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"] [data-component="placeholder"], [data-prosemirror-node-name="blockTaskItem"] [data-component="placeholder"]':
		{
			position: 'absolute',
			color: token('color.text.subtlest'),
			margin: `0 0 0 calc(${token('space.100')} * 3)`,
			pointerEvents: 'none',
			textOverflow: 'ellipsis',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			maxWidth: 'calc(100% - 50px)',
			display: 'none',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	"[data-prosemirror-node-name='taskItem']:has([data-empty]):not(:has([data-type-ahead])) [data-component='placeholder'], [data-prosemirror-node-name='blockTaskItem']:has([data-empty]):not(:has([data-type-ahead])) [data-component='placeholder']":
		{
			display: 'block',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-prosemirror-node-name="taskItem"] [data-component="content"], [data-prosemirror-node-name="blockTaskItem"] [data-component="content"]':
		{
			margin: 0,
			wordWrap: 'break-word',
			minWidth: 0,
			flex: '1 1 auto',
		},
});
